/**
 * LayerManager.ts;
 * Gestionnaire des calques et de l'organisation des éléments;
 * PHASE 1.2: Utilise StateService comme source de vérité
 */
import Konva from 'konva';
import { logger } from './utils/ProfessionalLogger';
import type { CanvasManager } from './types/LegacyCanvasManagerTypes';
import { useAppStore } from './services/StateService';

export interface LayerInfo {
  id: string;
  name: string;
  visible: boolean;
  locked: boolean;
  opacity: number;
  order: number;
  elements: Konva.Node[];
}

export class LayerManager {
  private layers: Map<string, LayerInfo> = new Map();
  private currentLayerId: string | null = null;
  private layerOrder: string[] = [];
  private isInitialized: boolean = false;
  private canvasManager: CanvasManager | null = null;

  constructor(canvasManager?: CanvasManager) {
    this.canvasManager = canvasManager || null;
    this.initializeDefaultLayer();
  }

  /**
   * Définit le CanvasManager
   */
  public setCanvasManager(canvasManager: CanvasManager): void {
    this.canvasManager = canvasManager;
  }

  /**
   * Obtient le CanvasManager
   */
  public getCanvasManager(): CanvasManager | null {
    return this.canvasManager;
  }
  /**
   * Initialise le LayerManager;
   */
  public initialize(): void {
    if (this.isInitialized) {
      logger.warn('LayerManager déjà initialisé');
      return;
    }
    this.isInitialized = true;
    logger.info('📚 LayerManager initialisé');
  }
  /**
   * Initialise le calque par défaut;
   */
  private initializeDefaultLayer(): void {
    const defaultLayer: LayerInfo = {
      id: 'default',
      name: 'Calque 1',
      visible: true,
      locked: false,
      opacity: 1,
      order: 0,
      elements: [],
    };
    this.layers.set('default', defaultLayer);
    this.currentLayerId = 'default';
    this.layerOrder.push('default');
  }
  /**
   * Ajoute un nouveau calque;
   */
  public addLayer(name: string, id?: string): string {
    const layerId = id || this.generateLayerId();
    const order = this.layerOrder.length;
    const newLayer: LayerInfo = {
      id: layerId,
      name,
      visible: true,
      locked: false,
      opacity: 1,
      order,
      elements: [],
    };
    this.layers.set(layerId, newLayer);
    this.layerOrder.push(layerId);
    logger.info(`➕ Calque ajouté: ${name} (${layerId})`);
    return layerId;
  }
  /**
   * Supprime un calque;
   */
  public removeLayer(layerId: string): boolean {
    if (layerId === 'default') {
      logger.warn('⚠️ Impossible de supprimer le calque par défaut');
      return false;
    }
    if (this.layers.has(layerId)) {
      this.layers.delete(layerId);
      this.layerOrder = this.layerOrder.filter(id => id !== layerId);
      if (this.currentLayerId === layerId) {
        this.currentLayerId = this.layerOrder[0] || 'default';
      }
      logger.info(`➖ Calque supprimé: ${layerId}`);
      return true;
    }
    return false;
  }
  /**
   * Met à jour les informations d'un calque;
   */
  public updateLayer(layerId: string, updates: Partial<LayerInfo>): boolean {
    const layer = this.layers.get(layerId);
    if (!layer) return false;
    Object.assign(layer, updates);
    logger.info(`📝 Calque mis à jour: ${layerId}`);
    return true;
  }
  /**
   * Obtient un calque par son ID;
   */
  public getLayer(layerId: string): LayerInfo | null {
    return this.layers.get(layerId) || null;
  }
  /**
   * Obtient tous les calques;
   */
  public getLayers(): LayerInfo[] {
    return this.layerOrder
      .map(id => this.layers.get(id))
      .filter((layer): layer is LayerInfo => layer !== undefined);
  }
  /**
   * Obtient le nombre de calques;
   */
  public getLayerCount(): number {
    return this.layers.size;
  }
  /**
   * Définit le calque actuel;
   */
  public setCurrentLayer(layerId: string): boolean {
    if (this.layers.has(layerId)) {
      this.currentLayerId = layerId;
      logger.info(`🎯 Calque actuel: ${layerId}`);
      return true;
    }
    return false;
  }
  /**
   * Obtient le calque actuel;
   */
  public getCurrentLayer(): LayerInfo | null {
    return this.currentLayerId ? this.layers.get(this.currentLayerId) || null : null;
  }
  /**
   * Met un calque au premier plan;
   * PHASE 1.2: Synchronise avec StateService
   */
  public bringToFront(layerId: string): boolean {
    // PHASE 1.2: Utiliser StateService pour réorganiser
    const store = useAppStore.getState();
    store.moveShapeToTop(layerId);

    // Mettre à jour le Map interne pour compatibilité
    if (!this.layers.has(layerId)) return false;
    this.layerOrder = this.layerOrder.filter(id => id !== layerId);
    this.layerOrder.push(layerId);
    this.layerOrder.forEach((id, index) => {
      const layer = this.layers.get(id);
      if (layer) layer.order = index;
    });
    logger.info(`⬆️ Calque mis au premier plan: ${layerId}`);
    return true;
  }
  /**
   * Met un calque à l'arrière-plan;
   * PHASE 1.2: Synchronise avec StateService
   */
  public sendToBack(layerId: string): boolean {
    // PHASE 1.2: Utiliser StateService pour réorganiser
    const store = useAppStore.getState();
    store.moveShapeToBottom(layerId);

    // Mettre à jour le Map interne pour compatibilité
    if (!this.layers.has(layerId)) return false;
    this.layerOrder = this.layerOrder.filter(id => id !== layerId);
    this.layerOrder.unshift(layerId);
    this.layerOrder.forEach((id, index) => {
      const layer = this.layers.get(id);
      if (layer) layer.order = index;
    });
    logger.info(`⬇️ Calque mis à l'arrière-plan: ${layerId}`);
    return true;
  }
  /**
   * Déplace un calque vers le haut;
   * PHASE 1.2: Synchronise avec StateService
   */
  public moveLayerUp(layerId: string): boolean {
    // PHASE 1.2: Utiliser StateService pour réorganiser
    const store = useAppStore.getState();
    store.moveShapeUp(layerId);

    // Mettre à jour le Map interne pour compatibilité
    const currentIndex = this.layerOrder.indexOf(layerId);
    if (currentIndex === -1 || currentIndex === this.layerOrder.length - 1) return false;
    [this.layerOrder[currentIndex], this.layerOrder[currentIndex + 1]] = [
      this.layerOrder[currentIndex + 1],
      this.layerOrder[currentIndex],
    ];
    this.layerOrder.forEach((id, index) => {
      const layer = this.layers.get(id);
      if (layer) layer.order = index;
    });
    logger.info(`⬆️ Calque déplacé vers le haut: ${layerId}`);
    return true;
  }
  /**
   * Déplace un calque vers le bas;
   * PHASE 1.2: Synchronise avec StateService
   */
  public moveLayerDown(layerId: string): boolean {
    // PHASE 1.2: Utiliser StateService pour réorganiser
    const store = useAppStore.getState();
    store.moveShapeDown(layerId);

    // Mettre à jour le Map interne pour compatibilité
    const currentIndex = this.layerOrder.indexOf(layerId);
    if (currentIndex === -1 || currentIndex === 0) return false;
    [this.layerOrder[currentIndex], this.layerOrder[currentIndex - 1]] = [
      this.layerOrder[currentIndex - 1],
      this.layerOrder[currentIndex],
    ];
    this.layerOrder.forEach((id, index) => {
      const layer = this.layers.get(id);
      if (layer) layer.order = index;
    });
    logger.info(`⬇️ Calque déplacé vers le bas: ${layerId}`);
    return true;
  }
  /**
   * Affiche/masque un calque;
   * PHASE 1.2: Synchronise avec StateService
   */
  public toggleLayerVisibility(layerId: string): boolean {
    // PHASE 1.2: Utiliser StateService comme source de vérité
    // layerId correspond à un shapeId dans StateService
    const store = useAppStore.getState();
    const shape = store.shapes.find(s => s.id === layerId);
    if (!shape) {
      // Fallback: utiliser le Map interne si la shape n'existe pas dans le Store
      const layer = this.layers.get(layerId);
      if (!layer) return false;
      layer.visible = !layer.visible;
      logger.info(`👁️ Visibilité du calque ${layerId}: ${layer.visible ? 'visible' : 'masqué'}`);
      return true;
    }

    // Mettre à jour dans StateService
    store.updateShape(layerId, { visible: !shape.visible });
    logger.info(`👁️ Visibilité du calque ${layerId}: ${!shape.visible ? 'visible' : 'masqué'}`);
    return true;
  }
  /**
   * Verrouille/déverrouille un calque;
   * PHASE 1.2: Synchronise avec StateService
   */
  public toggleLayerLock(layerId: string): boolean {
    // PHASE 1.2: Utiliser StateService comme source de vérité
    const store = useAppStore.getState();
    const shape = store.shapes.find(s => s.id === layerId);
    if (!shape) {
      // Fallback: utiliser le Map interne si la shape n'existe pas dans le Store
      const layer = this.layers.get(layerId);
      if (!layer) return false;
      layer.locked = !layer.locked;
      logger.info(
        `🔒 Verrouillage du calque ${layerId}: ${layer.locked ? 'verrouillé' : 'déverrouillé'}`
      );
      return true;
    }

    // Mettre à jour dans StateService
    store.updateShape(layerId, { locked: !shape.locked });
    logger.info(
      `🔒 Verrouillage du calque ${layerId}: ${!shape.locked ? 'verrouillé' : 'déverrouillé'}`
    );
    return true;
  }
  /**
   * Définit l'opacité d'un calque;
   */
  public setLayerOpacity(layerId: string, opacity: number): boolean {
    const layer = this.layers.get(layerId);
    if (!layer) return false;
    layer.opacity = Math.max(0, Math.min(1, opacity));
    logger.info(`🎨 Opacité du calque ${layerId}: ${layer.opacity}`);
    return true;
  }
  /**
   * Vide tous les calques;
   */
  public clearLayers(): void {
    this.layers.forEach(layer => {
      layer.elements = [];
    });
    logger.info('🧹 Tous les calques vidés');
  }
  /**
   * Rafraîchit la liste des calques;
   */
  public refreshLayersList(): void {
    // Cette méthode pourrait être utilisée pour mettre à jour l'UI;
    logger.info(`📚 Liste des calques rafraîchie: ${this.layers.size} calques`);
  }
  /**
   * Obtient les statistiques des calques;
   */
  public getLayerStatistics(): {
    totalLayers: number;
    visibleLayers: number;
    lockedLayers: number;
    totalElements: number;
  } {
    let visibleLayers = 0;
    let lockedLayers = 0;
    let totalElements = 0;
    this.layers.forEach(layer => {
      if (layer.visible) visibleLayers++;
      if (layer.locked) lockedLayers++;
      totalElements += layer.elements.length;
    });
    return {
      totalLayers: this.layers.size,
      visibleLayers,
      lockedLayers,
      totalElements,
    };
  }
  /**
   * Génère un ID de calque unique;
   */
  private generateLayerId(): string {
    // Utiliser crypto si disponible pour une meilleure unicité
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `layer_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`;
    }
    // Fallback avec crypto.getRandomValues
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const array = new Uint32Array(1);
      crypto.getRandomValues(array);
      return `layer_${Date.now()}_${array[0].toString(36)}`;
    }
    // Fallback final
    return `layer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  /**
   * Vérifie si le LayerManager est initialisé
   */
  public isLayerManagerInitialized(): boolean {
    return this.isInitialized;
  }
  /**
   * Nettoyage des ressources;
   */
  public destroy(): void {
    this.layers.clear();
    this.layerOrder = [];
    this.currentLayerId = null;
    this.isInitialized = false;
    logger.info('🧹 LayerManager détruit');
  }
  /**
   * Génère un nom d'objet unique;
   */
  public generateObjectName(type: string): string {
    const baseName = type.charAt(0).toUpperCase() + type.slice(1);
    const existingNames = Array.from(this.layers.values())
      .flatMap(layer => layer.elements)
      .map(element => element.name())
      .filter(name => name && name.startsWith(baseName));
    let counter = 1;
    let name = `${baseName} ${counter}`;
    while (existingNames.includes(name)) {
      counter++;
      name = `${baseName} ${counter}`;
    }
    return name;
  }
  /**
   * Remet à zéro les compteurs d'objets;
   */
  public resetCounters(): void {
    // Logique pour remettre à zéro les compteurs si nécessaire;
    logger.info("🔄 Compteurs d'objets remis à zéro");
  }
  /**
   * Invalide le thumbnail d'un objet;
   */
  public invalidateThumbnail(node: Konva.Node): void {
    // Logique pour invalider le thumbnail;
    logger.info(`🖼️ Thumbnail invalidé pour ${node.id()}`);
  }
  /**
   * Vide le cache des thumbnails;
   */
  public clearThumbnailCache(): void {
    // Logique pour vider le cache;
    logger.info('🗑️ Cache des thumbnails vidé');
  }

  /**
   * ===== GESTION DU Z-INDEX (LE "SANDWICH") =====
   * Architecture du sandwich :
   * - Z-Index 0-9 : Fond (background images, couleurs)
   * - Z-Index 10-19 : Placeholders photos (1, 2, 3, 4)
   * - Z-Index 20+ : Décorations (textes, logos, cadres, stickers)
   */
  /**
   * PHASE 3.8: Vérifie si un déplacement entre strates est autorisé
   * Empêche qu'un fond passe devant une photo, ou qu'une photo passe devant un overlay
   */
  private canMoveBetweenLayers(
    currentLayerType: ZIndexLayerType | null,
    targetLayerType: ZIndexLayerType
  ): boolean {
    // Règles de validation :
    // - Background ne peut pas aller dans Photo ou Decoration
    // - Photo ne peut pas aller dans Background ou Decoration
    // - Decoration peut aller partout (mais c'est rarement souhaitable)

    if (currentLayerType === 'background' && targetLayerType !== 'background') {
      logger.warn('⚠️ Un fond ne peut pas être déplacé devant une photo ou un overlay');
      return false;
    }

    if (currentLayerType === 'photo' && targetLayerType !== 'photo') {
      logger.warn('⚠️ Une photo ne peut pas être déplacée dans le fond ou devant un overlay');
      return false;
    }

    return true;
  }

  /**
   * Définit le Z-index d'un élément selon son type de couche
   * CORRIGÉ: Transfère physiquement les nœuds entre les layers Konva (backgroundLayer, photosLayer, mainLayer)
   * au lieu de seulement changer le z-index, ce qui permet de mettre une décoration derrière une photo
   * PHASE 3.8: Ajoute une validation pour empêcher le mélange des strates
   */
  public setZIndexLayer(
    element: Konva.Node,
    layerType: ZIndexLayerType,
    customIndex?: number
  ): boolean {
    if (!element) {
      logger.warn('⚠️ Élément invalide pour setZIndexLayer');
      return false;
    }

    // PHASE 3.8: Vérifier si le déplacement est autorisé
    const currentLayerType = this.getLayerType(element);
    if (!this.canMoveBetweenLayers(currentLayerType, layerType)) {
      return false;
    }

    let zIndex: number;

    if (customIndex !== undefined) {
      // Z-index personnalisé fourni
      zIndex = customIndex;
    } else {
      // Z-index automatique selon le type
      switch (layerType) {
        case 'background':
          // Z-index 0-9 pour le fond
          zIndex = this.getNextBackgroundZIndex();
          break;
        case 'photo':
          // Z-index 10-19 pour les placeholders photos
          zIndex = this.getNextPhotoZIndex();
          break;
        case 'decoration':
          // Z-index 20+ pour les décorations
          zIndex = this.getNextDecorationZIndex();
          break;
        default:
          logger.warn(`⚠️ Type de couche inconnu: ${layerType}`);
          return false;
      }
    }

    // Valider que le Z-index est dans la bonne plage
    if (!this.validateZIndex(zIndex, layerType)) {
      logger.warn(`⚠️ Z-index ${zIndex} invalide pour le type ${layerType}`);
      return false;
    }

    // CORRIGÉ: Transférer physiquement le nœud vers le layer Konva approprié
    // Cela permet de mettre une décoration (mainLayer) derrière une photo (photosLayer)
    if (this.canvasManager) {
      const currentParent = element.getParent();
      let targetLayer: Konva.Layer | null = null;

      // Déterminer le layer Konva cible selon le type
      switch (layerType) {
        case 'background':
          targetLayer = this.canvasManager.getBackgroundLayer();
          break;
        case 'photo':
          targetLayer = this.canvasManager.getPhotosLayer();
          break;
        case 'decoration':
          targetLayer = this.canvasManager.getMainLayer();
          break;
      }

      // Transférer le nœud vers le layer approprié si nécessaire
      if (targetLayer && currentParent !== targetLayer) {
        // CORRIGÉ: Vérifier que element est un Group ou Shape avant d'ajouter
        if (element instanceof Konva.Group || element instanceof Konva.Shape) {
          // Sauvegarder la position absolue pour éviter les décalages
          const absPos = element.getAbsolutePosition();

          // Retirer le nœud de son layer actuel
          element.remove();

          // Ajouter au layer cible
          targetLayer.add(element);

          // Restaurer la position absolue
          element.setAbsolutePosition(absPos);

          logger.info(`🔄 Nœud ${element.id()} transféré vers ${layerType} layer`);
        } else {
          logger.warn(`⚠️ Impossible de transférer le nœud ${element.id()}: type non supporté`);
        }
      }
    }

    // Définir le Z-index sur l'élément
    element.setAttr('zIndex', zIndex);
    element.zIndex(zIndex);

    // Stocker le type de couche pour référence
    element.setAttr('layerType', layerType);

    // Si c'est une décoration (Z-index >= 20), désactiver listening
    // pour permettre de cliquer "à travers" les overlays
    if (layerType === 'decoration' || zIndex >= 20) {
      element.listening(false);
      logger.debug(`🎨 Overlay configuré (listening: false) pour Z-index ${zIndex}`);
    } else {
      // Réactiver listening pour les autres couches
      element.listening(true);
    }

    logger.info(`📐 Z-index ${zIndex} défini pour ${layerType} (${element.id()})`);
    return true;
  }

  /**
   * Déplace un élément vers l'arrière-plan (couche background)
   */
  public moveToBackground(element: Konva.Node): boolean {
    return this.setZIndexLayer(element, 'background');
  }

  /**
   * Déplace un élément vers la couche photo
   */
  public moveToPhotoLayer(element: Konva.Node, photoIndex?: number): boolean {
    if (photoIndex !== undefined) {
      // Z-index spécifique pour une photo (10 + index)
      return this.setZIndexLayer(element, 'photo', 10 + photoIndex);
    }
    return this.setZIndexLayer(element, 'photo');
  }

  /**
   * Déplace un élément vers le premier plan (couche decoration)
   */
  public moveToForeground(element: Konva.Node): boolean {
    return this.setZIndexLayer(element, 'decoration');
  }

  /**
   * Obtient le prochain Z-index disponible pour le fond
   */
  private getNextBackgroundZIndex(): number {
    // Chercher le Z-index le plus élevé dans la plage 0-9
    const maxZ = this.getMaxZIndexInRange(0, 9);
    return Math.min(maxZ + 1, 9); // Ne pas dépasser 9
  }

  /**
   * Obtient le prochain Z-index disponible pour les photos
   */
  private getNextPhotoZIndex(): number {
    // Chercher le Z-index le plus élevé dans la plage 10-19
    const maxZ = this.getMaxZIndexInRange(10, 19);
    return Math.min(maxZ + 1, 19); // Ne pas dépasser 19
  }

  /**
   * Obtient le prochain Z-index disponible pour les décorations
   */
  private getNextDecorationZIndex(): number {
    // Chercher le Z-index le plus élevé dans la plage 20+
    const maxZ = this.getMaxZIndexInRange(20, 999);
    return maxZ + 1;
  }

  /**
   * Obtient le Z-index maximum dans une plage donnée
   */
  private getMaxZIndexInRange(min: number, max: number): number {
    if (!this.canvasManager) {
      return min;
    }

    // Récupérer tous les éléments du canvas
    interface CanvasManagerWithGetStage {
      getStage?: () => Konva.Stage | null;
    }
    const stage = (this.canvasManager as CanvasManagerWithGetStage).getStage?.();
    if (!stage) {
      return min;
    }

    let maxZ = min;
    stage.find('*').forEach((node: Konva.Node) => {
      const zIndex = node.getAttr('zIndex') || node.zIndex() || 0;
      if (zIndex >= min && zIndex <= max && zIndex > maxZ) {
        maxZ = zIndex;
      }
    });

    return maxZ;
  }

  /**
   * Valide qu'un Z-index est dans la bonne plage pour son type
   */
  private validateZIndex(zIndex: number, layerType: ZIndexLayerType): boolean {
    switch (layerType) {
      case 'background':
        return zIndex >= 0 && zIndex <= 9;
      case 'photo':
        return zIndex >= 10 && zIndex <= 19;
      case 'decoration':
        return zIndex >= 20;
      default:
        return false;
    }
  }

  /**
   * Obtient le Z-index actuel d'un élément
   */
  public getZIndex(element: Konva.Node): number {
    return element.getAttr('zIndex') || element.zIndex() || 0;
  }

  /**
   * Obtient le type de couche d'un élément
   */
  public getLayerType(element: Konva.Node): ZIndexLayerType | null {
    return element.getAttr('layerType') || null;
  }
}

/**
 * Types de couches pour le Z-index
 */
export type ZIndexLayerType = 'background' | 'photo' | 'decoration';
